import type { UserStateEntry, UserStateItemKey, UserStateMap } from "../../types/user-state";
import { normalizeUserStateVersion } from "../../utils/user-state/normalize-user-state-version";
import { buildUserStateItemApiPath } from "../../utils/user-state/build-user-state-item-api-path";
import type { UpdateUserStateMutationArgs, DeleteUserStateMutationArgs } from "./userStateOptimisticQuery";
import { takeJourneyFromResponse } from "../../src/modules/plus-journey/journey-events";

const USER_STATE_BASE_URL = "/api/user-state";

/**
 * Error shape thrown by the request helpers below on a non-2xx response — mirrors the
 * `{ error: { status, data } }` shape RTK Query's `fetchBaseQuery` produces, which is what
 * `redux/services/userStateOptimistic.ts`'s conflict handling (`error?.error?.status === 409`)
 * is written against. Keeping the same `{ status, data }` shape here (just without the extra
 * `error` wrapper — see `userStateOptimisticQuery.ts`'s `isUserStateConflictError`) lets the
 * TanStack port reuse the exact same 409-conflict-detection logic.
 */
export interface UserStateApiError {
  status: number;
  data?: unknown;
}

function isUserStateApiError(value: unknown): value is UserStateApiError {
  return typeof value === "object" && value !== null && "status" in value;
}

export { isUserStateApiError };

async function parseJsonBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(`${USER_STATE_BASE_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : undefined),
      ...init.headers,
    },
  });

  const body = await parseJsonBody(res);

  if (!res.ok) {
    const error: UserStateApiError = { status: res.status, data: body };
    throw error;
  }

  return body as T;
}

/** GET /api/user-state — the full user-state map. */
export function fetchUserState(): Promise<UserStateMap> {
  return request<UserStateMap>("", { method: "GET" });
}

/** GET /api/user-state/{...path} — a single item. */
export function fetchUserStateItem(itemKey: UserStateItemKey): Promise<UserStateEntry> {
  return request<UserStateEntry>(buildUserStateItemApiPath(itemKey), { method: "GET" });
}

/** PUT /api/user-state/{...path} — create/update a single item. */
export async function putUserStateItem({
  itemKey,
  data,
  version,
}: UpdateUserStateMutationArgs): Promise<UserStateEntry> {
  const entry = await request<UserStateEntry>(buildUserStateItemApiPath(itemKey), {
    method: "PUT",
    body: JSON.stringify({ data, version: normalizeUserStateVersion(version) }),
  });
  // Journey (event time): a save response may carry one nudge. It is published to the toast bridge
  // and kept out of the cached entry.
  return takeJourneyFromResponse(entry);
}

/** DELETE /api/user-state/{...path} — remove a single item. */
export function deleteUserStateItem({ itemKey, version }: DeleteUserStateMutationArgs): Promise<void> {
  const v = normalizeUserStateVersion(version);
  const suffix = v > 0 ? `?version=${v}` : "";
  return request<void>(`${buildUserStateItemApiPath(itemKey)}${suffix}`, { method: "DELETE" });
}
